<script setup lang="ts">
import { followTopic, getTopic, unfollowTopic } from '~~/src/api/topics'
import type { Topic } from '~~/src/types/topic'
import { CONTAINER_MAX_WIDTH } from '@/layouts/default.vue'

interface IProps {
  topicIdOrName: string
  showActionButtons?: boolean
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    showActionButtons: false,
  },
)

const { colors } = useTheme()
const { firebaseUser } = useFirebaseAuth()
const user = useUser()
const filters = useFilters()

const { topicIdOrName } = toRefs(props)

// Get from request in the future
const isFollowed = ref<null | boolean>(false)

// Add getTopic in the future
const topic = ref<null | Topic>(null)
const isProcessing = ref(false)

const router = useRouter()

const loadTopic = async () => {
  const resp = await getTopic(topicIdOrName.value)
  topic.value = resp
  isFollowed.value = resp?.isFollowed || false
}

await loadTopic()

watch(topicIdOrName, loadTopic)
watch(firebaseUser, loadTopic)

const handleFollow = async () => {
  if (!user.value)
    return router.push(useGetLoginURL())

  if (!topic.value || isProcessing.value)
    return

  isProcessing.value = true
  let success = false
  try {
    if (isFollowed.value)
      success = await unfollowTopic(user.value.id, topic.value.id)
    else
      success = await followTopic(user.value.id, topic.value.id)
  }
  catch (e) {
    console.error(e)
  }

  if (success)
    isFollowed.value = !isFollowed.value

  isProcessing.value = false
  loadFollowedTopics()
}
</script>

<template>
  <div
    v-if="topic"
    flex="~ col" gap-4
    pt-14
    px-4 md:px-6 lg:px-8
    mx-auto
    :style="{
      maxWidth: `${CONTAINER_MAX_WIDTH}px`,
    }"
  >
    <div class="flex items-center gap-4">
      <Heading v-if="topic">
        {{ `${topic.emoji} ${topic.name}` }}
      </Heading>

      <AppButton :enabled="!isProcessing" :style="{ opacity: isProcessing ? .8 : 1, minWidth: '62px' }" @click="handleFollow">
        <div v-if="isProcessing" animate-spin :style="{ animationDuration: '2s' }">
          <IconSpinner :size="20" color="currentColor" class="text" />
        </div>
        <template v-else>
          {{ isFollowed ? 'Unfollow' : 'Follow' }}
        </template>
      </AppButton>
    </div>

    <div class="flex flex-wrap gap-8">
      <div>
        <Heading level="4">
          <template v-if="filters.type_ids?.includes(PROMPT_TYPES.Visual)">
            {{ formatThousands(topic.nVisualPrompts) }}
          </template>
          <template v-else>
            {{ formatThousands(topic.nTextPrompts) }}
          </template>
        </Heading>

        <Text :color="colors.bluishDarker" variant="small">
          Prompts
        </Text>
      </div>
      <div>
        <Heading level="4">
          {{ formatThousands(topic.nFollowers) }}
        </Heading>

        <Text :color="colors.bluishDarker" variant="small">
          Followers
        </Text>
      </div>
      <Text max-w-md variant="normal" :color="colors.bluishDarker">
        {{ topic.description }}
      </Text>
    </div>
  </div>
</template>
